/**
 * DiscoveryRadar — evergreen half of the home "Lifestyle dossier".
 * Always has something to show: a daily rotating set of real UAE places
 * from the Explore map, each with a 1-tap Bucket List save. Read-only; no points.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Compass, MapPin, ArrowUpRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AddToBucketListButton } from "@/components/common/AddToBucketListButton";

interface RadarPlace {
  id: string;
  name: string;
  emirate: string;
  category: string;
  photoUrl: string | null;
}

/** Seasonal emirate-escape line — UAE seasons, presentation only. */
function seasonLine(): string {
  const m = new Date().getMonth();
  if (m >= 10 || m <= 2) return "Cool season escapes — the best months for desert, mountains and the coast.";
  if (m <= 4) return "Late-spring picks — golden evenings before the summer heat.";
  return "Summer edit — indoor gems and early-morning escapes across the Emirates.";
}

/** Stable daily pick so the radar changes once a day, not on every render. */
function pickForToday<T>(items: T[], count: number): T[] {
  if (items.length <= count) return items;
  const day = Math.floor(Date.now() / 86_400_000);
  const start = (day * 7) % items.length;
  return Array.from({ length: count }, (_, i) => items[(start + i * 5) % items.length]);
}

export function DiscoveryRadar() {
  const { data = [], isLoading } = useQuery({
    queryKey: ["discoveryRadar"],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<RadarPlace[]> => {
      const { data, error } = await (supabase as any)
        .from("map_places")
        .select("id, name, emirate, category, photo_url")
        .eq("is_active", true)
        .limit(120);
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        name: r.name,
        emirate: r.emirate,
        category: r.category,
        photoUrl: r.photo_url ?? null,
      }));
    },
  });

  const picks = useMemo(() => pickForToday(data, 4), [data]);
  const [lead, ...rest] = picks;

  return (
    <section
      aria-label="Discovery radar"
      className="gd-surface relative overflow-hidden rounded-[32px] p-6"
    >
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative">
        <p className="gd-kicker flex items-center gap-1.5">
          <Compass className="h-3.5 w-3.5" /> Discovery radar
        </p>
        <h3 className="gd-headline mt-2 text-2xl sm:text-3xl text-foreground">Worth a weekend</h3>
        <p className="gd-deck mt-1.5">{seasonLine()}</p>

        {isLoading && (
          <div className="mt-5 space-y-3" aria-hidden>
            <div className="h-40 rounded-2xl bg-muted animate-pulse" />
            <div className="h-14 rounded-2xl bg-muted animate-pulse" />
          </div>
        )}

        {lead && (
          <article className="mt-5 overflow-hidden rounded-2xl gd-well">
            <div className="relative h-56 lg:h-64 bg-gradient-to-br from-primary/25 via-primary/10 to-secondary/20">
              {lead.photoUrl && (
                <img src={lead.photoUrl} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              )}
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-background/85">
                  {lead.emirate} · {lead.category}
                </span>
                <p className="font-outfit text-lg font-bold leading-tight text-background line-clamp-2">{lead.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3">
              <AddToBucketListButton name={lead.name} area={lead.emirate} className="flex-1 rounded-full" />
              <Link
                to={`/explore?emirate=${encodeURIComponent(lead.emirate)}`}
                aria-label={`See ${lead.name} on the map`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/60 text-foreground transition-transform active:scale-[0.96]"
              >
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        )}

        {rest.length > 0 && (
          <ul className="mt-3 space-y-2.5">
            {rest.map((p) => (
              <li key={p.id} className="flex items-center gap-3 gd-well rounded-2xl p-3">
                <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-primary">
                  {p.photoUrl ? <img src={p.photoUrl} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" /> : <MapPin className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-outfit text-sm font-semibold text-foreground">{p.name}</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{p.emirate}</span>
                </span>
                <AddToBucketListButton name={p.name} area={p.emirate} compact variant="outline" className="h-10 w-10 rounded-full px-0" />
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !lead && (
          <Link to="/explore" className="mt-5 block rounded-2xl gd-well p-5">
            <p className="font-outfit text-lg font-bold text-foreground">The map is waiting.</p>
            <p className="gd-meta mt-1">Browse places across all seven Emirates.</p>
          </Link>
        )}

        <Link to="/explore" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          Open the full map <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}
